import { dynamicImports } from "@/Core/DynamicImports";
import { RegionType, SelectedType, } from "@/UI/Navigation/TreeView/TreeInterfaces";
import { FileInfo } from "@/FileSystem/FileInfo";
import { getFormatInfoGivenType } from "@/FileSystem/LoadSaveMolModels/Types/MolFormats";
import { convertIAtomsToIFileInfoPDB } from "@/FileSystem/LoadSaveMolModels/ConvertMolModels/_ConvertIAtoms";
import { getMoleculesFromStore } from "@/Store/StoreExternalAccess";
import { HydrogenDisplayType, } from "@/Core/Styling/SelAndStyleInterfaces";
import { getNamedPastelColor } from "@/Core/Styling/Colors/ColorUtils";
import { waitForCondition } from "@/Core/Utils/MiscUtils";
import { ViewerParent } from "./ViewerParent";
/**
 * Viewer3DMol
 */
export class Viewer3DMol extends ViewerParent {
    _mol3dObj; // public to make debugging easier
    // private _zoomToModelsTimeout: any;
    // Track the pending render request frame ID
    _renderRequest = null;
    _atomClicked = false;
    // Keep track of labels on regions so you can show and hide them with the
    // region itself.
    _regionLabels = {};
    // Track mouse down position to prevent click events on drag
    _mouseDownPosition = null;
    /**
     * Timer handle for removing the temporary dummy model used during
     * region-inclusive zooms. Tracked so it can be cancelled when a newer
     * zoom supersedes the current one.
     */
    _zoomTempModelTimer = null;
    /**
     * Reference to the temporary 3Dmol model created for region bounding
     * box zooms, kept so a superseding zoom can remove it immediately
     * instead of waiting for the timer.
     */
    _zoomTempModel = null;
    /**
     * requestAnimationFrame id of the in-flight camera tween, or null when no
     * animation is running. We animate the camera ourselves (see _animateView)
     * because 3Dmol's animated zoomTo/center run an untracked setTimeout loop
     * that cannot be stopped; holding this id lets a new zoom or any viewer
     * interaction cancel the motion cleanly.
     */
    _viewTweenRAF = null;
    /**
     * Whether an animated camera motion (zoomTo/center) is currently running.
     * Lets _cancelMotion skip work when nothing is animating, avoiding a
     * redundant setView/render on every wheel or mousedown event.
     */
    _motionInFlight = false;
    /**
     * Timer that clears _motionInFlight once a motion's animation duration has
     * elapsed, since 3Dmol gives no completion callback for zoomTo/center.
     */
    _motionTimer = null;
    /**
     * Removes a model from the viewer.
     *
     * @param  {string} id  The id of the model to remove.
     */
    _removeModel(id) {
        // remove from viewer
        const mol = this.lookup(id);
        if (mol) {
            this._mol3dObj.removeModel(mol);
            // Remove from cache too
            this.molCache[id] = null;
            delete this.molCache[id];
        }
    }
    /**
     * Removes a region from the viewer.
     *
     * @param  {string} id  The id of the region to remove.
     * @returns {void}
     */
    _removeRegion(id) {
        // remove from viewer
        const region = this.lookup(id);
        if (region && typeof region !== "string") {
            this._mol3dObj.removeShape(region);
            // Also remove label
            this.destroyRegionLabel(id);
        }
    }
    /**
     * Removes a surface from the viewer.
     *
     * @param  {GenericSurfaceType} surface  The surface to remove.
     */
    removeSurface(surface) {
        // Note: This is also used when you hide the associated molecule (to
        // remove the surface).
        this._mol3dObj.removeSurface(surface);
    }
    /**
     * Hide a model.
     *
     * @param  {string} id  The model to hide.
     */
    hideMolecule(id) {
        const model = this.lookup(id);
        if (model) {
            model.hide();
            // also remove it to free up memory, at the cost of speed.
            this._removeModel(id);
        }
    }
    /**
     * Hide a region.
     *
     * @param  {string} id  The region to hide.
     */
    hideRegion(id) {
        const region = this.lookup(id);
        if (region && typeof region !== "string") {
            region.hidden = true;
            this.destroyRegionLabel(id);
        }
    }
    /**
     * Show a model.
     *
     * @param  {string} id  The id of the model to show.
     */
    showMolecule(id) {
        const model = this.lookup(id);
        if (model) {
            model.show();
            // this.renderAll();
        }
    }
    /**
     * Show a region.
     *
     * @param  {string} id  The id of the region to show.
     * @param  {number} opacity  The opacity to show the region at.
     */
    showRegion(id, opacity) {
        const region = this.lookup(id);
        if (region && typeof region !== "string") {
            region.hidden = false;
            region.opacity = opacity;
            // Note that makeRegionLabel called separately from parent.
        }
    }
    /**
     * Create a label for a region.
     *
     * @param {string} id    The id of the region.
     * @param {string} text  The text of the label.
     */
    createRegionLabel(id, text) {
        const region = this.lookup(id);
        if (region && typeof region !== "string") {
            // console.log("CREATE: ", id, text);
            // Delete old label
            this.destroyRegionLabel(id);
            // Make new one
            this._regionLabels[id] = this.addLabel(text, region.x, region.y, region.z, "center", 14);
        }
    }
    /**
     * Destroy a region label.
     *
     * @param {string} id  The id of the region.
     */
    destroyRegionLabel(id) {
        // Delete old label
        if (this._regionLabels[id]) {
            console.log("DESTROY: ", id);
            this.removeLabel(this._regionLabels[id]);
            delete this._regionLabels[id];
        }
    }
    /**
     * Clear the current molecular styles.
     *
     * @param  {string} id  The id of the model to clear the styles of.
     */
    clearMoleculeStyles(id) {
        const model = this.lookup(id);
        if (model && model.setStyle) {
            // because regions don't have setStyle
            model.setStyle({}, {});
        }
    }
    /**
     * Sets the style of a molecule. TODO: good to not use any type for selection.
     *
     * @param  {string}     id            The id of the model to set the style of.
     * @param  {any}        selection     The selection to apply the style to.
     * @param  {GenericStyleType}  style  The style to apply.
     * @param  {boolean}    [add]         Whether to add the style to the existing
     *                                    styles. If false, replaces the existing
     *                                    style. Defaults to false.
     */
    setMolecularStyle(id, selection, style, add = false) {
        const model = this.lookup(id);
        if (model && model.setStyle) {
            // Apply the base style first. This will apply to all atoms in the selection,
            // including hydrogens.
            model.setStyle(selection, style, add);
            // Handle Hydrogen Display options
            if (style.hydrogens) {
                if (style.hydrogens === HydrogenDisplayType.None) {
                    // Hide all hydrogens within the current selection
                    // { ...selection, elem: 'H' } selects only H atoms within the original scope
                    // {} with default add=false replaces style (hides them)
                    model.setStyle({ ...selection, elem: "H" }, {}, false);
                }
                else if (style.hydrogens === HydrogenDisplayType.Polar) {
                    // For Polar, we want to hide non-polar hydrogens.
                    // Strategy:
                    // 1. Hide ALL hydrogens first (clears the style applied above for H atoms)
                    model.setStyle({ ...selection, elem: "H" }, {}, false);
                    // 2. Identify Polar Hydrogens manually
                    // We need to look up atoms to check bonds.
                    // Get all atoms in the model to resolve bond indices
                    const allAtoms = model.selectedAtoms({});
                    // Get atoms specifically in the current selection
                    const selectedAtoms = model.selectedAtoms(selection);
                    const polarHIndices = [];
                    const polarElems = new Set(["N", "O", "S"]);
                    for (const atom of selectedAtoms) {
                        if (atom.elem === "H" && atom.bonds) {
                            for (const bondIdx of atom.bonds) {
                                const neighbor = allAtoms[bondIdx];
                                // Ensure neighbor exists and is polar
                                if (neighbor && polarElems.has(neighbor.elem)) {
                                    polarHIndices.push(atom.index);
                                    break; // Found a polar bond, this H is polar
                                }
                            }
                        }
                    }
                    // 3. Re-apply the style ONLY to Polar Hydrogens
                    if (polarHIndices.length > 0) {
                        // use add=true to layer this style on top of the hidden/empty style set in step 1
                        model.setStyle({ index: polarHIndices }, style, true);
                    }
                }
                // If 'all', we don't need to do anything as the base style applied above
                // naturally includes all hydrogens.
            }
        }
    }
    /**
     * Adds a surface to the given model.
     *
     * @param  {string}     id     The id of the model to add the surface to.
     * @param  {GenericStyleType}  style  The style of the surface.
     * @returns {Promise<GenericSurfaceType>}  A promise that resolves when the
     *     surface.
     */
    _addSurface(id, style) {
        // NOTE: any in the promise is the surface object.
        const model = this.lookup(id);
        let atomSelectionForSurface = { model: model }; // Default to all atoms in the model
        // Check if a more specific selection is provided in ISelAndStyle
        // The selection object from ISelAndStyle (e.g., {resn: "LYS"})
        // is compatible with 3Dmol.js selection specification when
        // model context is also provided.
        if (style.selection && Object.keys(style.selection).length > 0) {
            atomSelectionForSurface = {
                ...this.convertSelection(style.selection), // convertSelection is currently identity
                model: model, // Ensure the model context is part of the selection
            };
        }
        return this._mol3dObj.addSurface(2, // surface type $3Dmol.SurfaceType.MS (Molecular Surface)
        style.surface, // style for the surface itself (e.g., color, opacity)
        atomSelectionForSurface);
    }
    /**
     * Updates the style of a surface.
     *
     * @param {string} id The id of the model.
     * @param {GenericStyleType} style The new style.
     */
    updateSurfaceStyle(id, style) {
        const surfaceIds = this.surfaces[id];
        if (surfaceIds) {
            for (const surfaceId of surfaceIds) {
                this._mol3dObj.setSurfaceMaterialStyle(surfaceId, style.surface);
            }
            this.renderAll();
        }
    }
    /**
     * Adds a model to the viewer. Returns same model, but now it's been added
     * to viewer.
     *
     * @param  {GLModel} model  The model to add.
     * @returns {Promise<GLModel>}  The model that was added.
     */
    addModel(model) {
        if (model.name === undefined &&
            model.contents === undefined) {
            // This should rarely occur. The model should be an IFileInfo with
            // the text of the model. But if loading old MolModa files, it might
            // be IAtom[]. Convert to pdb text so these old files load.
            model = convertIAtomsToIFileInfoPDB(model);
        }
        // Get the format
        const typ = new FileInfo(model).getFileType();
        const format = getFormatInfoGivenType(typ);
        const ext = format?.primaryExt;
        const newMol = this._mol3dObj.addModel(model.contents, ext, {
            keepH: true,
        });
        if (newMol.atoms.length === 0) {
            // Remove the empty model through 3Dmol's own removeModel API rather
            // than reassigning this._mol3dObj.models. Swapping the array out from
            // under 3Dmol leaves its internal model id/index bookkeeping pointing
            // at the old collection, so zoomTo (which resolves a numeric `model`
            // selector by indexing that collection) later reads an out-of-range
            // entry and fails. removeModel keeps that bookkeeping consistent.
            this._mol3dObj.removeModel(newMol);
        }
        // Remove all styles
        newMol.setStyle({}, {});
        return Promise.resolve(newMol);
    }
    /**
     * Adds a sphere to the viewer.
     *
     * @param  {ISphere} region  The sphere to add.
     * @returns {GenericRegionType}  The sphere that was added.
     */
    addSphere(region) {
        const sphere = this._mol3dObj.addSphere({
            center: {
                x: region.center[0],
                y: region.center[1],
                z: region.center[2],
            },
            radius: region.radius,
            color: region.color,
            opacity: region.opacity ?? 0.8,
        });
        return this._setRegionOpacity(sphere, region?.opacity).then(() => sphere);
    }
    /**
     * Adds a box to the viewer.
     *
     * @param  {IBox} region  The box to add.
     * @returns {GenericRegionType}  The box that was added.
     */
    addBox(region) {
        const dimens = region.dimensions;
        const center = region.center;
        const box = this._mol3dObj.addBox({
            corner: {
                x: center[0] - 0.5 * dimens[0],
                y: center[1] - 0.5 * dimens[1],
                z: center[2] - 0.5 * dimens[2],
            },
            dimensions: {
                w: dimens[0],
                h: dimens[1],
                d: dimens[2],
            },
            color: region.color,
            opacity: region.opacity ?? 0.8,
        });
        return this._setRegionOpacity(box, region?.opacity).then(() => box);
    }
    /**
     * Adds a arrow to the viewer.
     *
     * @param  {IArrow} region  The arrow to add.
     * @returns {GenericRegionType}  The arrow that was added.
     */
    addArrow(region) {
        const arrow = this._mol3dObj.addArrow({
            start: {
                x: region.center[0],
                y: region.center[1],
                z: region.center[2],
            },
            end: {
                x: region.endPt[0],
                y: region.endPt[1],
                z: region.endPt[2],
            },
            radius: region.radius,
            color: region.color,
            radiusRatio: region.radiusRatio,
            opacity: region.opacity ?? 0.8,
        });
        return this._setRegionOpacity(arrow, region?.opacity).then(() => arrow);
    }
    /**
     * Adds a cylinder to the viewer.
     *
     * @param  {ICylinder} region  The cylinder to add.
     * @returns {GenericRegionType}  The cylinder that was added.
     */
    addCylinder(region) {
        const cylinder = this._mol3dObj.addCylinder({
            start: {
                x: region.center[0],
                y: region.center[1],
                z: region.center[2],
            },
            end: {
                x: region.endPt[0],
                y: region.endPt[1],
                z: region.endPt[2],
            },
            radius: region.radius,
            color: region.color,
            fromCap: 2,
            toCap: 2,
            dashed: region.dashed,
            opacity: region.opacity ?? 0.8,
        });
        return this._setRegionOpacity(cylinder, region?.opacity).then(() => cylinder);
    }
    /**
     * Sets the opacity of a region after creation. Uses setTimeout because
     * 3Dmol.js requires a tick before opacity updates take effect on an
     * already-added shape.
     *
     * @param {any}                region    The region to set the opacity of.
     * @param {number | undefined} opacity  The opacity to set.
     * @returns {Promise<void>} A promise that resolves when the opacity is set and render called.
     */
    _setRegionOpacity(region, opacity) {
        return new Promise((resolve) => {
            setTimeout(() => {
                // Not sure why, but this needs to be in a setTimeout for the
                // opacity to actually change.
                region.opacity = opacity ?? 0.8;
                this.renderAll();
                resolve();
            }, 0);
        });
    }
    /**
     * Schedules a render of the viewer using requestAnimationFrame.
     * This coalesces multiple render calls within the same frame into a single
     * render, preventing unnecessary rebuilds and improving performance.
     *
     * @returns {Promise<void>} A promise that resolves immediately.
     */
    _renderAll() {
        if (this._mol3dObj) {
            // if (this._renderRequest) {
            //   cancelAnimationFrame(this._renderRequest);
            // }
            // this._renderRequest = requestAnimationFrame(() => {
            //   this._mol3dObj.render();
            //   this._renderRequest = null;
            // });
            // console.log(this._mol3dObj.models.map((m: any) => ({
            //   id: m.getID?.(),
            //   atoms: m.atoms?.length,
            //   hasModelData: !!m.modelData,
            //   ext: m.format,
            // })));
            this._mol3dObj.render();
        }
        return Promise.resolve();
    }
    /**
     * Immediately cleans up any temporary model and timer left over from
     * a previous zoom, so they don't linger when a new zoom supersedes.
     */
    _cleanupPreviousZoom() {
        if (this._zoomTempModelTimer !== null) {
            clearTimeout(this._zoomTempModelTimer);
            this._zoomTempModelTimer = null;
        }
        if (this._zoomTempModel !== null) {
            this._mol3dObj.removeModel(this._zoomTempModel);
            this._zoomTempModel = null;
        }
    }
    /**
     * Marks an animated camera motion as in flight for `durationMs`, so a later
     * interaction or zoom knows there is something to cancel.
     *
     * @param {number} durationMs  The motion's animation duration, in ms.
     */
    _beginMotion(durationMs) {
        this._motionInFlight = true;
        if (this._motionTimer !== null) {
            clearTimeout(this._motionTimer);
        }
        this._motionTimer = setTimeout(() => {
            this._motionInFlight = false;
            this._motionTimer = null;
        }, durationMs);
    }
    /**
     * Linearly interpolates two 3Dmol view arrays
     * ([posX, posY, posZ, zoom, qx, qy, qz, qw]). Translation and zoom
     * (indices 0-3) interpolate componentwise; the rotation quaternion
     * (indices 4-7) uses a normalized lerp with a shortest-path sign flip.
     * That is exact when the endpoints share an orientation, which is the
     * usual case here since fit-to-view zooms and centering never rotate the
     * scene.
     *
     * @param {number[]} a  Start view (from getView()).
     * @param {number[]} b  End view (from getView()).
     * @param {number}   t  Interpolation fraction in [0, 1].
     * @returns {number[]}  The interpolated 8-element view array.
     */
    _interpolateView(a, b, t) {
        const view = new Array(8);
        for (let i = 0; i < 4; i++) {
            view[i] = a[i] + (b[i] - a[i]) * t;
        }
        // Flip the end quaternion's sign on a negative dot product so we take the
        // shorter arc, then normalized-lerp the four components.
        const dot = a[4] * b[4] + a[5] * b[5] + a[6] * b[6] + a[7] * b[7];
        const sign = dot < 0 ? -1 : 1;
        const qx = a[4] + (sign * b[4] - a[4]) * t;
        const qy = a[5] + (sign * b[5] - a[5]) * t;
        const qz = a[6] + (sign * b[6] - a[6]) * t;
        const qw = a[7] + (sign * b[7] - a[7]) * t;
        const len = Math.hypot(qx, qy, qz, qw) || 1;
        view[4] = qx / len;
        view[5] = qy / len;
        view[6] = qz / len;
        view[7] = qw / len;
        return view;
    }
    /**
     * Animates the camera from one view to another with a self-owned
     * requestAnimationFrame loop. 3Dmol's built-in animated moves run an
     * untracked setTimeout chain that cannot be stopped, so overlapping motions
     * fight and jerk; because this loop stores its frame id, _cancelMotion can
     * stop it the instant a new zoom or a user interaction occurs.
     *
     * @param {number[]} startView   The view to animate from.
     * @param {number[]} endView     The view to animate to.
     * @param {number}   durationMs  Animation length in milliseconds.
     */
    _animateView(startView, endView, durationMs) {
        this._cancelMotion();
        const start = performance.now();
        const tick = (now) => {
            if (!this._mol3dObj) {
                // Viewer was unloaded mid-animation; abort.
                this._viewTweenRAF = null;
                return;
            }
            let t = (now - start) / durationMs;
            if (t >= 1) {
                t = 1;
            }
            // easeInOutCubic, to mimic the smooth start/stop of 3Dmol's own zoom.
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            this._mol3dObj.setView(this._interpolateView(startView, endView, eased));
            if (t < 1) {
                this._viewTweenRAF = requestAnimationFrame(tick);
            }
            else {
                this._viewTweenRAF = null;
            }
        };
        this._viewTweenRAF = requestAnimationFrame(tick);
    }
    /**
     * Cancels any in-flight self-owned camera tween. Safe to call when nothing
     * is animating.
     */
    _cancelMotion() {
        if (this._viewTweenRAF !== null) {
            cancelAnimationFrame(this._viewTweenRAF);
            this._viewTweenRAF = null;
        }
    }
    /**
     * Zoom in on a set of models. Captures the current zoom generation from
     * the parent so that if a newer zoom is requested while the 750ms
     * animation is in flight, the temporary model cleanup from this zoom
     * is handled correctly and the animation is effectively superseded.
     *
     * @param  {string[]} ids  The models to zoom in on.
     */
    async zoomToModels(ids) {
        // Capture the generation at the time this zoom was requested.
        const myGeneration = this._zoomGeneration;
        // Clean up any leftover temp model from a previous zoom that was
        // superseded before its cleanup timer fired.
        this._cleanupPreviousZoom();
        let models = [];
        let regions = [];
        const startTime = performance.now();
        await waitForCondition(() => {
            // If a newer zoom was requested while we were waiting for models
            // to appear, abandon this zoom entirely.
            if (this._zoomGeneration !== myGeneration) {
                return true;
            }
            models = [];
            regions = [];
            for (const id of ids) {
                const obj = this.lookup(id);
                if (obj) {
                    if (obj.selectedAtoms !== undefined) {
                        models.push(obj);
                    }
                    else if (this.regionDefinitionCache[id]) {
                        regions.push(this.regionDefinitionCache[id]);
                    }
                }
            }
            const foundSomething = models.length > 0 || regions.length > 0;
            const elapsedTime = performance.now() - startTime;
            return foundSomething || elapsedTime > 5000; // Wait for five seconds max.
        }, 100);
        // Bail out if this zoom has been superseded.
        if (this._zoomGeneration !== myGeneration) {
            return;
        }
        if (models.length === 0 && regions.length === 0) {
            return;
        }
        // 3Dmol's selector treats a numeric `model` entry as a direct index into
        // this._mol3dObj.models. getID() is a creation-order id that drifts past
        // the live array length once models are removed (e.g. hidden compounds),
        // and passing the GLModel object instead makes 3Dmol deep-copy the
        // selection, recursing through the model's circular atom/bond graph until
        // the stack overflows. So resolve each model to its CURRENT index in the
        // live models array, which is always in range and is a plain number.
        const modelIndexByObj = new Map();
        this._mol3dObj.models.forEach((m, idx) => {
            modelIndexByObj.set(m, idx);
        });
        const modelIdsToZoom = [];
        for (const m of models) {
            const idx = modelIndexByObj.get(m);
            if (idx !== undefined) {
                modelIdsToZoom.push(idx);
            }
        }
        let tempModel = null;
        // If regions are present, we calculate their bounding box and create a
        // temporary invisible model to encompass them, so we can zoom to it.
        if (regions.length > 0) {
            let minX = Infinity;
            let minY = Infinity;
            let minZ = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;
            let maxZ = -Infinity;
            for (const region of regions) {
                let rMinX, rMinY, rMinZ, rMaxX, rMaxY, rMaxZ;
                const c = region.center;
                if (region.type === RegionType.Sphere) {
                    const r = region.radius || 0;
                    rMinX = c[0] - r;
                    rMaxX = c[0] + r;
                    rMinY = c[1] - r;
                    rMaxY = c[1] + r;
                    rMinZ = c[2] - r;
                    rMaxZ = c[2] + r;
                }
                else if (region.type === RegionType.Box) {
                    const d = region.dimensions || [0, 0, 0];
                    const hw = d[0] / 2;
                    const hh = d[1] / 2;
                    const hd = d[2] / 2;
                    rMinX = c[0] - hw;
                    rMaxX = c[0] + hw;
                    rMinY = c[1] - hh;
                    rMaxY = c[1] + hh;
                    rMinZ = c[2] - hd;
                    rMaxZ = c[2] + hd;
                }
                else if (region.type === RegionType.Arrow ||
                    region.type === RegionType.Cylinder) {
                    const r = region.radius || 0;
                    const start = region.center;
                    const end = region.endPt;
                    rMinX = Math.min(start[0], end[0]) - r;
                    rMaxX = Math.max(start[0], end[0]) + r;
                    rMinY = Math.min(start[1], end[1]) - r;
                    rMaxY = Math.max(start[1], end[1]) + r;
                    rMinZ = Math.min(start[2], end[2]) - r;
                    rMaxZ = Math.max(start[2], end[2]) + r;
                }
                else {
                    // Fallback
                    rMinX = c[0];
                    rMaxX = c[0];
                    rMinY = c[1];
                    rMaxY = c[1];
                    rMinZ = c[2];
                    rMaxZ = c[2];
                }
                if (rMinX < minX)
                    minX = rMinX;
                if (rMinY < minY)
                    minY = rMinY;
                if (rMinZ < minZ)
                    minZ = rMinZ;
                if (rMaxX > maxX)
                    maxX = rMaxX;
                if (rMaxY > maxY)
                    maxY = rMaxY;
                if (rMaxZ > maxZ)
                    maxZ = rMaxZ;
            }
            // Create a dummy model using XYZ format to define the bounding box corners.
            // PDB format is strict about column positions, XYZ is safer for dynamic values.
            if (Number.isFinite(minX) && Number.isFinite(maxX)) {
                const xyzText = `2
Dummy Model for Zoom
C ${minX} ${minY} ${minZ}
C ${maxX} ${maxY} ${maxZ}`;
                tempModel = this._mol3dObj.addModel(xyzText, "xyz");
                // IMPORTANT: We must give it a style (e.g. sphere) so 3Dmol considers
                // it a valid renderable object for zooming. However, we set opacity to 0
                // so the user doesn't see these dummy atoms.
                tempModel.setStyle({}, { sphere: { radius: 0.1, opacity: 0 } });
                // addModel appends to the live array, so look up its current index
                // rather than relying on getID().
                const idx = this._mol3dObj.models.indexOf(tempModel);
                if (idx !== -1) {
                    modelIdsToZoom.push(idx);
                }
            }
        }
        if (this._zoomGeneration !== myGeneration) {
            if (tempModel) {
                this._mol3dObj.removeModel(tempModel);
            }
            return;
        }
        // If every resolved ID was invalid, there is nothing 3Dmol can zoom
        // to. Skip the call entirely rather than let it crash in
        // getAtomsFromSel.
        if (modelIdsToZoom.length === 0) {
            if (tempModel) {
                this._mol3dObj.removeModel(tempModel);
            }
            return;
        }
        try {
            // Let 3Dmol compute the destination view instantly (duration 0 does the
            // bounding-box math without animating), capture it, snap back to the
            // start, then animate there with a cancelable self-owned tween. 3Dmol's
            // animated zoomTo runs an untracked setTimeout loop that cannot be
            // stopped, so two overlapping animated zooms fight over the camera and
            // jerk; owning the loop is what actually fixes the bounce.
            const startView = this._mol3dObj.getView();
            this._mol3dObj.zoomTo({ model: modelIdsToZoom }, 0);
            const endView = this._mol3dObj.getView();
            this._mol3dObj.setView(startView);
            this._animateView(startView, endView, 750);
        }
        catch (err) {
            // 3Dmol's zoomTo can throw if a referenced model was removed
            // between lookup and invocation (e.g. a concurrent removeObjects).
            // Swallow here so a transient race does not surface as an
            // uncaught TypeError to the user.
            console.warn("zoomTo failed; skipping zoom animation.", err);
            if (tempModel) {
                this._mol3dObj.removeModel(tempModel);
                tempModel = null;
            }
        }
        if (tempModel) {
            // Track the temp model so a superseding zoom can remove it
            // immediately rather than waiting for the timer.
            this._zoomTempModel = tempModel;
            this._zoomTempModelTimer = setTimeout(() => {
                // Only clean up if this zoom is still the current one.
                if (this._zoomGeneration === myGeneration) {
                    this._mol3dObj.removeModel(tempModel);
                }
                this._zoomTempModel = null;
                this._zoomTempModelTimer = null;
            }, 800);
        }
    }
    /**
     * Zoom in on a specific point.
     *
     * @param  {number} x  The x coordinate.
     * @param  {number} y  The y coordinate.
     * @param  {number} z  The z coordinate.
     */
    centerOnPoint(x, y, z) {
        // Stop any in-flight zoom first; a concurrent zoom and this centering
        // motion would otherwise fight over the camera and jerk.
        this.cancelZoom();
        // this._mol3dObj.zoomTo({ x: x, y: y, z: z }, 500, true);
        // Same instant-compute-then-tween approach as zoomToModels, so the
        // centering animation is cancelable rather than an uncancelable 3Dmol
        // setTimeout loop.
        const startView = this._mol3dObj.getView();
        this._mol3dObj.center({ x: x, y: y, z: z }, 0, false);
        const endView = this._mol3dObj.getView();
        this._mol3dObj.setView(startView);
        this._animateView(startView, endView, 500);
    }
    /**
     * Adds a label to the viewer
     *
     * @param  {string} lblTxt       The text of the label.
     * @param  {number} x            The x coordinate.
     * @param  {number} y            The y coordinate.
     * @param  {number} z            The z coordinate.
     * @param  {string} [alignment]  The alignment of the label. Defaults to
     *                               "bottomCenter".
     * @param  {number} [fontSize]   The font size of the label. Defaults to 18.
     * @param  {boolean} [inFront]   Whether the label should be in front of the
     *                               molecule. Defaults to true.
     * @returns {GenericLabelType}  The label.
     */
    addLabel(lblTxt, x, y, z, alignment = "bottomCenter", fontSize = 18, inFront = true) {
        return this._mol3dObj.addLabel(lblTxt, 
        // https://3dmol.csb.pitt.edu/doc/types.html#LabelSpec
        {
            position: { x: x, y: y, z: z },
            backgroundColor: "white",
            fontColor: "black",
            borderThickness: 1,
            borderColor: "black",
            backgroundOpacity: 0.9,
            // screenOffset: $3Dmol.Vector2(10, 10),
            inFront: inFront,
            alignment: alignment, // 'bottomLeft'
            fontSize: fontSize,
        });
    }
    /**
     * Removes a label from the viewer.
     *
     * @param  {GenericLabelType} label  The label to remove.
     */
    removeLabel(label) {
        this._mol3dObj.removeLabel(label);
    }
    /**
     * Load 3dmol.js dynamically, set the viewer object, etc.
     *
     * @param  {string} id  The HTML Dom id of the element to load the viewer
     *                      into.
     * @returns {Promise<any>}  A promise that resolves the viewer object when
     *     3dmol.js is loaded.
     */
    async _loadAndSetupViewerLibrary(id) {
        const $3Dmol = await dynamicImports.mol3d.module;
        // Wait for the DOM element to be present before attempting to create the viewer.
        await waitForCondition(() => document.getElementById(id) !== null, 100);
        const viewer = $3Dmol.createViewer(id, {
            defaultcolors: $3Dmol.rasmolElementColors,
        });
        // If viewer creation fails even after the DOM element is present, it's
        // likely a critical error. But let's keep waiting.
        await waitForCondition(() => viewer !== null && viewer !== undefined, 100);
        viewer.setBackgroundColor(0xffffff);
        this._mol3dObj = viewer;
        // Changing the thickness of the fog doesn't seem to be
        // possible. Also examined the libarry code with llm, which
        // confirmed fog near/far values cannot be set externally.
        this._mol3dObj.enableFog(true);
        // Adding subtle outline makes things easier to see.
        viewer.setViewStyle({ style: "outline", width: 0.02 });
        // Any direct interaction with the canvas (rotate, pan, scroll-zoom,
        // touch) should abort an in-flight camera animation; otherwise the
        // user's motion and the animation fight and the view jerks.
        const canvas = viewer.getCanvas();
        if (canvas) {
            const cancelOnInteract = () => {
                this.cancelZoom();
            };
            canvas.addEventListener("mousedown", cancelOnInteract);
            canvas.addEventListener("wheel", cancelOnInteract, { passive: true });
            canvas.addEventListener("touchstart", cancelOnInteract, {
                passive: true,
            });
        }
        return this;
    }
    /**
     * Converts the 3DMoljs style stored in the molecules tree to a style format
     * compatible with this viewer.
     *
     * @param {ISelAndStyle}   style     The style to convert.
     * @param {TreeNode} treeNode  The treenode associated with the style.
     * @returns {ISelAndStyle}  The converted style.
     */
    convertStyle(style, treeNode) {
        // Process style if it contains any @byMolecule colors
        if (treeNode.id !== undefined && this._containsByMoleculeColor(style)) {
            const processedStyle = this._processStyleColors(style, treeNode.id);
            style = processedStyle;
        }
        return style;
    }
    /**
     * Recursively checks if a style object contains any @byMolecule colors
     *
     * @param {any} obj The object to check
     * @returns {boolean} Whether the object contains any @byMolecule colors
     */
    _containsByMoleculeColor(obj) {
        if (!obj || typeof obj !== "object") {
            return false;
        }
        return Object.entries(obj).some(([_, value]) => {
            void _;
            if (value === "@byMolecule") {
                return true;
            }
            if (typeof value === "object") {
                return this._containsByMoleculeColor(value);
            }
            return false;
        });
    }
    /**
     * Recursively processes a style object, replacing any @byMolecule colors
     * with the actual molecule color
     *
     * @param {any}    obj         The style object to process
     * @param {string} moleculeId  The ID of the molecule to get the color for
     * @returns {Record<string, any>} The processed style object
     */
    _processStyleColors(obj, moleculeId) {
        if (!obj || typeof obj !== "object") {
            return obj;
        }
        const result = Array.isArray(obj) ? [] : {};
        let allMols = undefined;
        Object.entries(obj).forEach(([key, value]) => {
            if (value === "@byMolecule") {
                if (allMols === undefined) {
                    allMols = getMoleculesFromStore().flattened;
                }
                const treeNode = allMols.find((node) => node.id === moleculeId);
                let colorId = moleculeId;
                if (treeNode !== undefined) {
                    // Associated treenode found. Get it's top-most ancestor.
                    const topAncestor = treeNode.getAncestry(allMols).nodes[0];
                    colorId = topAncestor.id || moleculeId;
                }
                result[key] = getNamedPastelColor(colorId);
            }
            else if (typeof value === "object") {
                result[key] = this._processStyleColors(value, moleculeId);
            }
            else {
                result[key] = value;
            }
        });
        return result;
    }
    /**
     * Converts a 3DMoljs selection to the selection format compatible with this
     * viewer.
     *
     * @param {any} sel  The selection to convert.
     * @returns {any}  The converted selection.
     */
    convertSelection(sel) {
        // Already in 3Dmoljs format, so no conversion needed.
        return sel;
    }
    /**
     * Gets the data uri of the current view (png).
     *
     * @returns {Promise<string>}  A promise that resolves to the data uri.
     */
    pngURI() {
        return Promise.resolve(this._mol3dObj.pngURI());
    }
    /**
     * Unloads the viewer (from the DOM, etc.).
     */
    unLoad() {
        // this._mol3dObj.clear();
        this._mol3dObj = null;
    }
    /**
     * Makes atoms NOT responsive to mouse hovering and clicking.
     *
     * @param {GLModel} model  The model to make atoms NOT hoverable and
     *                         clickable.
     */
    makeAtomsNotClickable(model) {
        model.setClickable({}, false);
        // Above doesn't remove the callback, which I think can take up quite a
        // bit of space. Let's remove it. NOTE: In practice, this occasionally
        // causes an error.
        // model.selectedAtoms({}).forEach((atom: any) => {
        //     if (atom.callback) {
        //         delete atom.callback;
        //     }
        // });
    }
    /**
     * Makes atoms react when clicked.
     *
     * @param {GLModel}  model     The model to make clickable.
     * @param {Function} callBack  Function that runs when atom is clicked.
     *                             Receives the clicked atom's chain, resi,
     *                             resn, name, and coordinates.
     */
    makeAtomsClickable(model, callBack) {
        // Remove existing clickable
        this.makeAtomsNotClickable(model);
        model.setClickable({}, true, (atom /* _viewer: any, _event: any, _container: any */) => {
            this._atomClicked = true;
            setTimeout(() => {
                this._atomClicked = false;
            }, 200);
            this.centerOnPoint(atom.x, atom.y, atom.z);
            // Capture atom metadata now; the 3Dmol atom object may be mutated
            // before the delayed callback fires.
            const info = {
                moleculeId: "", // populated by caller (ViewerParent)
                chain: atom.chain,
                resi: atom.resi,
                resn: atom.resn,
                atomName: atom.atom,
                x: atom.x,
                y: atom.y,
                z: atom.z,
            };
            setTimeout(() => {
                // Delay the callback so that the centering has time to
                // finish.
                callBack(info);
            }, 1000);
        });
        this.renderAll();
    }
    /**
     * Makes atoms NOT react when mouse moves over then (NOT hoverable).
     *
     * @param {GLModel} model  The model to make NOT hoverable.
     */
    makeAtomsNotHoverable(model) {
        model.setHoverable({}, false);
        // Below doesn't remove callbacks. They take up quite a bit of memory, I
        // think. Let's remove them. NOTE: In practice, this occasionally causes
        // an error.
        // model.selectedAtoms({}).forEach((atom: any) => {
        //     if (atom.hover_callback) {
        //         delete atom.hover_callback;
        //     }
        //     if (atom.unhover_callback) {
        //         delete atom.unhover_callback;
        //     }
        // });
    }
    /**
     * Makes atoms react when mouse moves over then (hoverable).
     *
     * @param {GLModel}  model               The model to make hoverable.
     * @param {Function} onHoverInCallBack   Function that runs when hover over
     *                                       atom starts.
     * @param {Function} onHoverOutCallBack  Function that runs when hover over
     *                                       atom ends.
     */
    makeAtomsHoverable(model, onHoverInCallBack, onHoverOutCallBack) {
        this.makeAtomsNotHoverable(model);
        model.setHoverable({}, true, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (atom, viewer, _event, _container) => {
            if (!atom.label) {
                const lblTxt = this.hoverLabelText(atom.chain, atom.resn, atom.resi, atom.atom);
                if (lblTxt) {
                    atom.label = this.addLabel(lblTxt, atom.x, atom.y, atom.z);
                }
            }
            onHoverInCallBack(atom.x, atom.y, atom.z);
        }, 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (atom, _viewer, _event, _container) => {
            if (atom.label) {
                setTimeout(() => {
                    this.removeLabel(atom.label);
                    delete atom.label;
                }, 1000);
            }
            onHoverOutCallBack();
        });
    }
    /**
     * Gets a VRML model of the current scene. But not implemented for NGL.
     *
     * @returns {string}  The VRML string.
     */
    exportVRML() {
        // for (const model of this._mol3dObj.models) {
        //     this.makeAtomsHoverable(
        //         model,
        //         () => {
        //             alert("hi");
        //         },
        //         () => {
        //             alert("bye");
        //         }
        //     );
        // }
        return this._mol3dObj.exportVRML();
    }
    /**
     * Exports the VRML for each model in the viewer.
     *
     * @param {boolean} [simplify]  Whether to simplify the VRML. Defaults to false.
     * @returns {string[][]}  The VRML for each model.
     */
    async exportVRMLPerModel(simplify = false) {
        // const vrmls: {[id: string]: string} = {};
        const vrmls = [];
        let simpParams = {};
        let surfaceSimpParams = {};
        if (simplify === true) {
            // If simplify is true, we need to merge the vertices and reduce the
            // precision of the VRML, etc.
            simpParams = {
                mergeVertices: 0.1, // NOTE: 0.1 is lowest value that still looks good.
                removeOrphanVertexes: true,
                precision: 2, // NOTE: at 1, starts to degrade.
                sphereQuality: 1, // NOTE this is good enough
                cylinderSubdivisions: 3, // NOTE: minimum to get a decent cylinder
                cylinderHeightSegments: 4, // NOTE: must be even, four is good enough
                cartoonQuality: 5, // NOTE: Minimum to get a decent cartoon,
                minimizeWhiteSpace: true,
            };
            surfaceSimpParams = {
                mergeVertices: 0.5, // NOTE: 0.1 is lowest value that still looks good.
                precision: 2, // NOTE: at 1, starts to degrade.
                removeOrphanVertexes: true,
                minimizeWhiteSpace: true,
                simplifySurfaces: 0.7,
                smoothSurfaces: 1,
            };
        }
        // Get all the models
        for (const id in this.molCache) {
            const model = this.lookup(id);
            if (model) {
                vrmls.push([id, model.exportVRML(simpParams)]);
            }
        }
        // Do the same for surfaces
        for (const id in this.surfaces) {
            const surfaceIds = this.surfaces[id];
            for (const surfaceId of surfaceIds) {
                const surfaces = this._mol3dObj.getSurface(surfaceId);
                for (const surface of surfaces) {
                    vrmls.push([id, surface.lastGL.vrml(surfaceSimpParams)]);
                }
            }
        }
        return vrmls;
    }
    /**
     * Sets up a callback that runs every time the view changes.
     *
     * @param {Function} callback  The callback to run.
     */
    _registerViewChangeCallback(callback) {
        // NOTE: The below slows things down quite a bit, I think. Don't use it.
        // this._mol3dObj.setViewChangeCallback(callback);
        let lastViewSum = this.getView().reduce((a, b) => a + b, 0);
        setInterval(() => {
            const newView = this.getView();
            const newViewSum = newView.reduce((a, b) => a + b, 0);
            if (newViewSum !== lastViewSum) {
                lastViewSum = newViewSum;
                callback(newView);
            }
        }, 1000);
    }
    /**
     * Gets the current view.
     *
     * @returns {number[]}  The view.
     */
    getView() {
        return this._mol3dObj.getView();
    }
    /**
     * Sets the view.
     *
     * @param {number[]} view  The view to set.
     */
    setView(view) {
        this._mol3dObj.setView(view);
    }
    /**
     * Sets the viewer to be clickable on the background (empty space).
     *
     * @param {Function} callback The callback to run when the background is clicked.
     */
    setBackgroundClickable(callback) {
        if (!this._mol3dObj)
            return;
        const canvas = this._mol3dObj.getCanvas();
        // Track mouse down position
        canvas.addEventListener("mousedown", (event) => {
            this._mouseDownPosition = { x: event.clientX, y: event.clientY };
        });
        canvas.addEventListener("click", (event) => {
            // Check if mouse moved significantly
            if (this._mouseDownPosition) {
                const dx = event.clientX - this._mouseDownPosition.x;
                const dy = event.clientY - this._mouseDownPosition.y;
                // Use a small threshold (e.g. 3 pixels) to account for tiny movements during a click
                if (dx * dx + dy * dy > 9) {
                    return;
                }
            }
            setTimeout(() => {
                if (!this._atomClicked) {
                    callback();
                }
                // Ensure flag is reset in case atom click handler didn't fire or timing was off
                this._atomClicked = false;
            }, 50);
        });
    }
    /**
     * Sets (updates) the style of an existing region.
     *
     * @param {TreeNode} treeNode  The tree node containing the region to
     *        update.
     */
    updateRegionStyle(treeNode) {
        // Rather than update the region, we remove it and re-add it. This is
        // because the 3DMoljs viewer does not have a way to update the position
        // as best I can tell.
        const id = treeNode.id;
        const regionStyle = JSON.parse(JSON.stringify(treeNode.region));
        if (treeNode.selected !== SelectedType.False) {
            // yellow
            regionStyle.color = "#ffff00";
            regionStyle.opacity = regionStyle.opacity ?? 0.75;
        }
        // Always remove any old regions.
        this.addRegion(regionStyle)
            .then(async (region) => {
            // Remove previous region if it exists
            this._removeRegion(id);
            // add new region
            this.regionCache[id] = region;
            // IMPORTANT: Update the definition cache so zoomToModels has the correct
            // coordinates for the moved/resized region.
            this.regionDefinitionCache[id] = regionStyle;
            // Also remove previous region label
            this.destroyRegionLabel(id);
            // Add new region
            // const treeNode = getMoleculesFromStore().filters.onlyId(id);
            const title = treeNode?.title;
            this.createRegionLabel(id, title ?? "Region");
            return;
        })
            .catch((err) => {
            throw err;
        });
    }
    /**
     * Checks if two arrays are equal.
     *
     * @param {any[]} a First array.
     * @param {any[]} b Second array.
     * @returns {boolean} True if equal.
     */
    areArraysEqual(a, b) {
        if (a === b)
            return true;
        if (a == null || b == null)
            return false;
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i])
                return false;
        }
        return true;
    }
    /**
     * Gets the text to show when mouse hovers over an atom.
     *
     * @param {string} chain  The chain id.
     * @param {string} resn   The residue name.
     * @param {string} resi   The residue index.
     * @param {string} atomName  The atom name.
     * @returns {string | undefined}  The text to show, or undefined if no text
     *   should be shown.
     */
    hoverLabelText(chain, resn, resi, atomName) {
        const lbls = [];
        if (chain) {
            lbls.push(chain);
        }
        if (resn) {
            lbls.push(resn);
        }
        if (resi) {
            lbls.push(resi);
        }
        if (atomName) {
            lbls.push(atomName);
        }
        if (lbls.length > 0) {
            return lbls.join(":");
        }
        return undefined;
    }
}
